import { AnalyticsUserEvent } from '@fruitful/types';
import { type Href, router } from 'expo-router';
import * as React from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ScreenContainer,
    Spinner,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { track } from '../../../logger/Logger';
import useGetAllBankAccountDetailsFromGlobalState from '../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import {
    AccountVerificationLetterLink,
    useAccountVerificationLetterFlow,
} from '../account-verification-letter';
import { DetailsTileForAccount } from '../details-tile/DetailsTileForAccount';
import { BankGeneralDisclosureFootnote } from '../Disclosures';
import type { DirectDepositSwitchAppContext } from './types';

export const ManualSwitch = ({
    defaultBankAccountId,
    context = '',
    source = '',
}: {
    defaultBankAccountId?: string;
    context?: DirectDepositSwitchAppContext | '';
    source?: string;
}) => {
    const redirectHref: Href =
        context === 'roadmap' ? '/home/roadmap' : '/home/banking';

    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`${context}/${source}/DirectDeposit/ManualSwitch`);

    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();

    const defaultAccount = bankAccountDetails.find(
        (d) => d.bankAccountId === defaultBankAccountId,
    );

    const accountsToRender =
        defaultAccount == null ? bankAccountDetails : [defaultAccount];

    const { openSheet, flowElements } = useAccountVerificationLetterFlow({
        variant: 'direct-deposit',
        defaultBankAccountId,
    });

    return (
        <ScreenContainer
            title="Manual direct deposit setup"
            hasBackButton
            onFallbackRoute={redirectHref}
            onBack={() => {
                track(
                    AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_MANUAL_SWITCH_EXIT,
                    {
                        ddAppContext: context,
                        ddSource: source,
                    },
                );
                router.navigate(redirectHref);
            }}
        >
            <YStack gap={24} justifyContent="space-between">
                <YStack gap={24}>
                    <YStack gap={12}>
                        <XStack gap={10}>
                            <View width={15}>
                                <BodyMediumRegular textAlign="right">
                                    1.
                                </BodyMediumRegular>
                            </View>
                            <BodyMediumRegular textAlign="left">
                                Sign into your payroll provider&apos;s site.
                            </BodyMediumRegular>
                        </XStack>
                        <XStack gap={10}>
                            <View width={15}>
                                <BodyMediumRegular textAlign="right">
                                    2.
                                </BodyMediumRegular>
                            </View>
                            <BodyMediumRegular textAlign="left">
                                Enter the account and routing number of the
                                Fruitful deposit account where you want to send
                                your direct deposit.
                            </BodyMediumRegular>
                        </XStack>
                    </YStack>
                    {isLoadingBankAccountDetails ? (
                        <Spinner />
                    ) : (
                        <XStack
                            gap={10}
                            flexWrap="wrap"
                            justifyContent="center"
                        >
                            {accountsToRender.map((account) => {
                                return (
                                    <View
                                        key={`account_${account.bankAccountId}`}
                                        height={136}
                                    >
                                        <DetailsTileForAccount
                                            account={account}
                                        />
                                    </View>
                                );
                            })}
                        </XStack>
                    )}
                    <YStack gap={8}>
                        <BodyMediumRegular>
                            <BodyMediumEmphasized>
                                Routing number not recognized?
                            </BodyMediumEmphasized>{' '}
                            Send this account verification letter to your
                            payroll provider to set up direct deposit.{' '}
                            <AccountVerificationLetterLink onPress={openSheet}>
                                Download letter
                            </AccountVerificationLetterLink>
                        </BodyMediumRegular>
                    </YStack>
                </YStack>

                <YStack gap={16}>
                    <Button
                        text="Done"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        onPress={() => {
                            track(
                                AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_MANUAL_SWITCH_DONE,
                                {
                                    ddAppContext: context,
                                    ddSource: source,
                                },
                            );
                            router.navigate(redirectHref);
                        }}
                    />
                    <BankGeneralDisclosureFootnote />
                </YStack>
            </YStack>
            {flowElements}
        </ScreenContainer>
    );
};
